import { Typography, useTheme } from '@material-ui/core';
import React, { FC, useCallback, useState } from 'react';
import CourierDetailActionsButton from '../CourierDetailActionsButton';
import { EBottomSheetAction, TBottomSheetAction } from '../CourierBottomSheet';
import { useQueryClient } from 'react-query';
import {
  CourierDetailsActionsAddInput,
  ICourierDetailErrorProps,
} from '../CourierDetailsActionsAddInput';
import { useTranslation } from 'react-i18next';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { useLinkCourierMutation } from '../../queries';
import { LINK_COURIER_ERROR, TCourier } from '../../services/api';
import { trackInvitationSendStart, trackInvitationSendSuccess } from '../../tracking/invitation';
import { encryptHash } from '@src/shared/utils';
import { PageDivider } from '@src/shared/components/PageDivider';
import { NotificationCategories, useNotificationsActions } from '@src/shared/context/notifications';
import styles from './CourierDetailActionsAdd.module.scss';

interface ICourierDetailActionsAddProps {
  onActionSuccess: Function;
}

export const CourierDetailActionsAdd: FC<ICourierDetailActionsAddProps> = ({ onActionSuccess }) => {
  /* ReactQuery */
  const queryCache = useQueryClient();

  /* Context State */
  const { restaurant } = useKeycloakUserState();

  const { notifyInformation } = useNotificationsActions(NotificationCategories.CourierManagement);

  /* Component State */
  const [inputForm, setInputForm] = useState<{
    isValidated: boolean;
    data: any;
  }>();
  const [clearForm, setClearInputForm] = useState(false);
  const [isPromising, setIsPromising] = useState<boolean>();
  const [inputError, setInputError] = useState<ICourierDetailErrorProps>({
    reason: '',
    status: false,
  });

  const linkCourierMutation = useLinkCourierMutation({
    onSuccess: (data: TCourier) => {
      setClearInputForm(true);
      if (onActionSuccess) {
        onActionSuccess();
      }
      notifyInformation(t('manage_couriers.ADD_COURIER_SUCCESS_MESSAGE'), '');
      trackInvitationSendSuccess(
        restaurant?.language,
        restaurant?.country,
        encryptHash(data.email)
      );
      queryCache.invalidateQueries('couriers');
      setIsPromising(false);
    },
    onError: (error: any) => {
      const errorMsg: LINK_COURIER_ERROR =
        error.response?.data?.reason || LINK_COURIER_ERROR.INVALID_PARAMS;
      setInputError({
        reason: t(`addCourier.error_${errorMsg}`),
        status: true,
      });
      setIsPromising(false);
    },
  });

  const onLinkCourier = async () => {
    setIsPromising(true);
    //e.preventDefault();
    if (!inputForm?.isValidated) {
      return;
    }
    trackInvitationSendStart(
      restaurant?.language,
      restaurant?.country,
      encryptHash(inputForm.data)
    );
    linkCourierMutation.mutate({
      courierEmail: inputForm.data,
      restaurantName: restaurant?.name as string,
    });
  };

  /* Hooks */

  const { t } = useTranslation();
  const theme = useTheme();

  /* Component Event Handlers */
  const handleFormInputValidations = useCallback(
    (e: any) => {
      return setInputForm({ isValidated: e.isValidated, data: e.data });
    },
    [inputForm?.isValidated]
  );

  const handleOnButtonClick = (e: TBottomSheetAction) => {
    if (e.type === EBottomSheetAction.Send) {
      onLinkCourier();
    }
  };

  return (
    <>
      <div className={styles.courierDetailActionsAddContainer}>
        <div className={styles.courierDetailActionsAddSpace}>
          <Typography variant="h3" className="heading_m">
            {t('manage_couriers.ADD_COURIER_BUTTON')}
          </Typography>
        </div>
        <div className={styles.courierDetailActionsAddSpace}>
          <Typography variant="body1" className="subheading_s_n">
            {t('manage_couriers.ADD_COURIER_SUBTEXT')}
          </Typography>
        </div>
        <div className={styles.courierDetailActionsAddSpace}>
          <CourierDetailsActionsAddInput
            isEmailExists={inputError}
            clearForm={clearForm}
            onOutSideClickValidationCheck={handleFormInputValidations}
          />
        </div>
        <div className={styles.courierDetailActionsAddSpace}></div>
        <PageDivider
          onDeviceStyles={{ mobile: { marginTop: '0px' }, desktop: { marginTop: '8px' } }}
          styles={{
            backgroundColor: theme.palette.jet?.dividerDefault,
            height: '12px',
            flexShrink: '0',
            width: '100%',
          }}
        />
        <div className={`${styles.courierDetailActionsAddSpace} ${styles.clearMargin}`}>
          <CourierDetailActionsButton
            buttonText={t('manage_couriers.ADD_BUTTON_TEXT')}
            isDisabled={!inputForm?.isValidated}
            onButtonAction={handleOnButtonClick}
            actionType={EBottomSheetAction.Send}
            isLoading={isPromising}
          />
        </div>
      </div>
    </>
  );
};

export default CourierDetailActionsAdd;
